import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowDownRight, ArrowUpRight, CalendarRange, ChevronDown, ChevronUp, History, RefreshCcw, ShieldCheck } from 'lucide-react'
import { supabase } from '../lib/supabase'

type Shift={
 session_id:string;opened_at:string;closed_at:string;
 opening_amount:number;expected_amount:number;closing_amount:number;
 pix:number;debit_card:number;credit_card:number;cash:number;other_amount:number;
 total_received:number;payment_count:number;supply:number;withdrawal:number;
 calculated_cash:number;difference:number
}
const brl=new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'})
const n=(value:unknown)=>Number(value)||0
const local=(value:string)=>new Date(value).toLocaleString('pt-BR',{dateStyle:'short',timeStyle:'short'})
function statusLabel(value:number){
 if(Math.abs(value)<0.005)return 'Conferido'
 return value>0?'Sobra':'Falta'
}

export function CashShiftHistory(){
 const [shifts,setShifts]=useState<Shift[]>([])
 const [period,setPeriod]=useState('30')
 const [limit,setLimit]=useState('30')
 const [selected,setSelected]=useState<string|null>(null)
 const [loading,setLoading]=useState(true)
 const [error,setError]=useState('')
 const reload=useCallback(async()=>{
  setLoading(true);setError('')
  const {data,error:issue}=await supabase.rpc('get_cash_shift_history',{p_limit:Number(limit)})
  if(issue){setError(issue.message);setShifts([])}else setShifts((data||[]) as Shift[])
  setLoading(false)
 },[limit])
 useEffect(()=>{void reload()},[reload])
 const visible=useMemo(()=>{
  const cutoff=Date.now()-Number(period)*86400000
  return shifts.filter(shift=>new Date(shift.closed_at).getTime()>=cutoff)
 },[shifts,period])
 const totals=useMemo(()=>{
  return visible.reduce((acc,s)=>({
   received:acc.received+n(s.total_received),pix:acc.pix+n(s.pix),
   debit:acc.debit+n(s.debit_card),credit:acc.credit+n(s.credit_card),
   cash:acc.cash+n(s.cash),difference:acc.difference+n(s.difference),
   losses:acc.losses+Math.max(0,-n(s.difference)),surplus:acc.surplus+Math.max(0,n(s.difference))
  }),{received:0,pix:0,debit:0,credit:0,cash:0,difference:0,losses:0,surplus:0})
 },[visible])
 const avg=visible.length?totals.received/visible.length:0
 return <section className="panel cash3-history" aria-label="Histórico financeiro dos caixas encerrados">
  <div className="cash3-top">
   <div><span className="eyebrow dark">CAIXA 2.0 · TERCEIRA ETAPA</span><h2>Histórico e comparação de turnos</h2><p>Dados de caixas fechados. Os períodos são filtrados pela data de fechamento.</p></div>
   <div className="cash3-controls">
    <label>Período<select value={period} onChange={event=>setPeriod(event.target.value)}><option value="7">7 dias</option><option value="30">30 dias</option><option value="90">90 dias</option></select></label>
    <label>Turnos consultados<select value={limit} onChange={event=>setLimit(event.target.value)}><option value="15">15</option><option value="30">30</option><option value="60">60</option><option value="90">90</option></select></label>
    <button className="ghost-button" type="button" onClick={()=>void reload()} disabled={loading}><RefreshCcw size={16}/> Atualizar</button>
   </div>
  </div>
  {error&&<div className="form-error" role="alert">Não foi possível consultar o histórico: {error}</div>}
  {loading?<div className="loading">Conferindo turnos encerrados…</div>:!error&&!visible.length?<div className="empty-state compact"><History size={27}/><strong>Nenhum fechamento no período</strong><p>Escolha um intervalo maior para consultar caixas já encerrados.</p></div>:!error&&<>
   <div className="cash3-metrics">
    <div><small>Recebido nos turnos</small><strong>{brl.format(totals.received)}</strong><span>{visible.length} fechamento(s)</span></div>
    <div><small>Média por turno</small><strong>{brl.format(avg)}</strong><span>Recebimentos / turnos</span></div>
    <div><small>Total de sobras</small><strong>{brl.format(totals.surplus)}</strong><span>Diferenças positivas</span></div>
    <div><small>Total de faltas</small><strong>{brl.format(totals.losses)}</strong><span>Diferenças negativas</span></div>
   </div>
   <div className="cash3-breakdown">
    <span>Pix <strong>{brl.format(totals.pix)}</strong></span>
    <span>Dinheiro <strong>{brl.format(totals.cash)}</strong></span>
    <span>Débito <strong>{brl.format(totals.debit)}</strong></span>
    <span>Crédito <strong>{brl.format(totals.credit)}</strong></span>
   </div>
   <div className="cash3-list">
    {visible.map(shift=>{
      const expanded=selected===shift.session_id
      const difference=n(shift.difference)
      return <article className="cash3-shift" key={shift.session_id}>
       <button className="cash3-row" type="button" onClick={()=>setSelected(expanded?null:shift.session_id)} aria-expanded={expanded}>
        <span className="cash3-date"><CalendarRange size={18}/><span><strong>{local(shift.closed_at)}</strong><small>Aberto: {local(shift.opened_at)}</small></span></span>
        <span><small>Recebido</small><strong>{brl.format(n(shift.total_received))}</strong></span>
        <span><small>Diferença do caixa</small><strong className={Math.abs(difference)<.005?'cash3-ok':'cash3-warning'}>{statusLabel(difference)} {brl.format(Math.abs(difference))}</strong></span>
        {expanded?<ChevronUp size={18}/>:<ChevronDown size={18}/>}
       </button>
       {expanded&&<div className="cash3-detail">
        <div><small>Valor de abertura</small><strong>{brl.format(n(shift.opening_amount))}</strong></div>
        <div><small>Pix</small><strong>{brl.format(n(shift.pix))}</strong></div>
        <div><small>Débito</small><strong>{brl.format(n(shift.debit_card))}</strong></div>
        <div><small>Crédito</small><strong>{brl.format(n(shift.credit_card))}</strong></div>
        <div><small>Dinheiro recebido</small><strong>{brl.format(n(shift.cash))}</strong></div>
        <div><small>Outras formas</small><strong>{brl.format(n(shift.other_amount))}</strong></div>
        <div><small>Suprimentos</small><strong><ArrowUpRight size={14}/>{brl.format(n(shift.supply))}</strong></div>
        <div><small>Sangrias</small><strong><ArrowDownRight size={14}/>{brl.format(n(shift.withdrawal))}</strong></div>
        <div><small>Dinheiro esperado pela movimentação</small><strong>{brl.format(n(shift.calculated_cash))}</strong></div>
        <div><small>Valor esperado registrado</small><strong>{brl.format(n(shift.expected_amount))}</strong></div>
        <div><small>Dinheiro contado no fechamento</small><strong>{brl.format(n(shift.closing_amount))}</strong></div>
        <div><small>Pagamentos registrados</small><strong>{n(shift.payment_count)}</strong></div>
       </div>}
      </article>
    })}
   </div>
   <p className="cash3-note"><ShieldCheck size={15}/> Somente leitura: não altera caixa, pagamentos ou fechamento. A diferença histórica usa o valor esperado gravado no fechamento. Diferenças de períodos anteriores podem refletir regras antigas de movimentação; confira com os comprovantes.</p>
  </>}
 </section>
}
